/**
 * Die Uebersicht (KONZEPT.md §10, §14.2).
 *
 * Oben die eine Zahl, auf die es im Einsatz ankommt: wie viel greifbar ist.
 * Darunter, wie sich der Bestand auf Status und Verbleib verteilt, wie viel
 * gebucht wurde und wo Pruefungen anstehen. Zuletzt, was die Module beitragen.
 *
 * Jede Zahl, hinter der eine Liste steht, fuehrt dorthin.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { DashboardKachel, type Kennzahlen, type Verbleib } from "@thw/contracts";
import { anfrage } from "../../api/klient.js";
import { useStatusArten } from "../../lokal/bestand.js";
import {
  Karte,
  Ladeanzeige,
  Leer,
  Nummer,
  Rueckmeldung,
  VERBLEIB_TEXT,
  Wahl,
} from "../../ui/bausteine.jsx";
import {
  Balken,
  Kennzahl,
  Leitzahl,
  Saeulen,
  StapelBalken,
  TON,
  TonZeichen,
  zahl,
  type Teil,
} from "./diagramme.jsx";

type Zeitraum = "7" | "30" | "90";
const ZEITRAEUME: readonly { wert: Zeitraum; text: string }[] = [
  { wert: "7", text: "7 Tage" },
  { wert: "30", text: "30 Tage" },
  { wert: "90", text: "90 Tage" },
];

const VERBLEIB_REIHENFOLGE: readonly Verbleib[] = [
  "im_lager",
  "im_einsatz",
  "ausgeliehen",
  "nicht_erfasst",
];

interface ModulKachel {
  modul: string;
  id: string;
  titel: string;
  url: string;
}

export function Dashboard() {
  const [zeitraum, setZeitraum] = useState<Zeitraum>("30");
  const statusArten = useStatusArten();

  const { data, isPending, isError, isPlaceholderData } = useQuery({
    queryKey: ["kennzahlen", zeitraum],
    queryFn: () => anfrage<Kennzahlen>("GET", `/api/core/kennzahlen?tage=${zeitraum}`),
    // Beim Wechsel des Zeitraums bleibt das alte Bild stehen, bis das neue da
    // ist -- kein Springen, kein leerer Bildschirm.
    placeholderData: keepPreviousData,
  });

  const { data: module } = useQuery({
    queryKey: ["module"],
    queryFn: () => anfrage<{ dashboard: ModulKachel[] }>("GET", "/api/core/module"),
    staleTime: 10 * 60_000,
  });

  if (isPending) return <Ladeanzeige was="Die Übersicht" />;
  if (isError) {
    return (
      <Rueckmeldung
        art="still"
        kopf="Die Übersicht braucht Netz"
        zusatz="Buchen und Scannen gehen trotzdem."
      />
    );
  }

  const b = data.bestand;
  const statusTeile: Teil[] = statusArten
    .map((a) => ({
      name: a.bezeichnung,
      farbe: a.farbe,
      wert: b.nach_status.find((s) => s.status === a.schluessel)?.anzahl ?? 0,
      link: `/geraete?status=${a.schluessel}`,
    }))
    .filter((t) => t.wert > 0);
  // Ein Status, den es in den Stammdaten nicht (mehr) gibt, faellt nicht still heraus.
  for (const s of b.nach_status) {
    if (!statusArten.some((a) => a.schluessel === s.status)) {
      statusTeile.push({
        name: s.status,
        farbe: "var(--ohne)",
        wert: s.anzahl,
        link: `/geraete?status=${s.status}`,
      });
    }
  }

  const verbleib = VERBLEIB_REIHENFOLGE.map((v) => ({
    name: VERBLEIB_TEXT[v].replace(/^./, (c) => c.toUpperCase()),
    wert: b.nach_verbleib.find((x) => x.verbleib === v)?.anzahl ?? 0,
    link: `/geraete?verbleib=${v}`,
  }));

  const p = data.pruefungen;
  const ampel: Teil[] = [
    {
      name: "Überfällig",
      wert: p.ueberfaellig,
      link: "/geraete?pruefung=ueberfaellig",
      farbe: TON.kritisch.farbe,
      zeichen: <TonZeichen ton="kritisch" />,
    },
    {
      name: `In ${String(p.frist_tage)} Tagen fällig`,
      wert: p.bald_faellig,
      link: "/geraete?pruefung=bald_faellig",
      farbe: TON.warnung.farbe,
      zeichen: <TonZeichen ton="warnung" />,
    },
    {
      name: "In Ordnung",
      wert: p.in_ordnung,
      link: "/geraete?pruefung=in_ordnung",
      farbe: TON.gut.farbe,
      zeichen: <TonZeichen ton="gut" />,
    },
    {
      name: "Ohne Termin",
      wert: p.ohne_termin,
      link: "/geraete?pruefung=ohne_termin",
      farbe: TON.neutral.farbe,
      zeichen: (
        <span
          className="inline-flex size-5 items-center justify-center rounded-full bg-neutral-300 text-xs font-bold dark:bg-neutral-600"
          aria-label="ohne Termin"
        >
          –
        </span>
      ),
    },
  ];

  const reihen = [
    { name: "Bewegungen", farbe: "var(--reihe-1)" },
    { name: "Statusänderungen", farbe: "var(--reihe-2)" },
  ];
  const summeBewegungen = data.bewegungen.reihe.reduce(
    (s, t) => s + t.bewegungen + t.statusaenderungen,
    0,
  );

  return (
    <div
      className={`flex flex-col gap-4 transition-opacity ${isPlaceholderData ? "opacity-60" : ""}`}
    >
      <h1 className="text-2xl font-bold">Übersicht</h1>

      <Leitzahl
        bezeichnung="Greifbar"
        wert={b.verfuegbar}
        von={b.gesamt}
        erklaerung="Einsatzbereit und im Lager. Im Einsatz, ausgeliehen oder defekt zählt nicht."
        link="/geraete?verfuegbar=ja"
      />

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kennzahl bezeichnung="Bestand" wert={b.gesamt} link="/geraete" />
        <Kennzahl
          bezeichnung="Einsatzbereit"
          wert={b.einsatzbereit}
          link="/geraete?einsatzbereit=ja"
        />
        <Kennzahl
          bezeichnung="Nicht einsatzbereit"
          wert={b.gesamt - b.einsatzbereit}
          ton={b.gesamt - b.einsatzbereit > 0 ? "warnung" : "neutral"}
          link="/geraete?einsatzbereit=nein"
        />
        <Kennzahl
          bezeichnung="Prüfung überfällig"
          wert={p.ueberfaellig}
          ton={p.ueberfaellig > 0 ? "kritisch" : "gut"}
          link="/geraete?pruefung=ueberfaellig"
        />
      </div>

      <Karte titel="Status">
        <StapelBalken teile={statusTeile} einheit="Artikel" />
        {b.archiviert > 0 && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Dazu {zahl(b.archiviert)} archiviert (verloren, gestohlen …) — nicht im Bestand.
          </p>
        )}
      </Karte>

      <Karte titel="Wo ist der Bestand?">
        <Balken eintraege={verbleib} />
      </Karte>

      <Karte titel="Buchungen">
        <Wahl
          beschriftung="Zeitraum"
          wert={zeitraum}
          moeglichkeiten={ZEITRAEUME}
          aendern={setZeitraum}
        />
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          {zahl(summeBewegungen)} Buchungen in den letzten {zeitraum} Tagen. Was ein Packstück
          mitnimmt, zählt einmal.
        </p>
        <Saeulen
          tage={data.bewegungen.reihe.map((t) => ({
            datum: t.datum,
            werte: [t.bewegungen, t.statusaenderungen],
          }))}
          reihen={reihen}
        />
      </Karte>

      <Karte titel="Prüfungen">
        <StapelBalken teile={ampel} einheit="Artikel" />
        {p.naechste.length > 0 && (
          <>
            <h3 className="mt-2 font-semibold">Als Nächstes fällig</h3>
            <ul className="flex flex-col gap-1">
              {p.naechste.map((n) => (
                <li key={`${n.artikel_id}-${n.pruefung}`}>
                  <Link
                    to={`/artikel/${n.artikel_id}`}
                    className="flex min-h-12 items-center justify-between gap-3 rounded-lg px-2 active:bg-neutral-100 dark:active:bg-neutral-700"
                  >
                    <span className="flex flex-col">
                      <span className="font-semibold">{n.bezeichnung}</span>
                      <span className="text-sm text-neutral-600 dark:text-neutral-300">
                        <Nummer>{n.inventarnummer}</Nummer> · {n.pruefung}
                      </span>
                    </span>
                    <FaelligAm datum={n.faellig} />
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </Karte>

      {(module?.dashboard ?? []).map((k) => (
        <ModulKarte key={`${k.modul}-${k.id}`} kachel={k} />
      ))}

      <p className="text-center text-xs text-neutral-500 dark:text-neutral-400">
        Stand {new Date(data.stand).toLocaleString("de-DE")}
      </p>
    </div>
  );
}

function FaelligAm({ datum }: { datum: string }) {
  const heute = new Date().toISOString().slice(0, 10);
  const ueber = datum < heute;
  return (
    <span className="flex shrink-0 items-center gap-2 text-sm font-semibold">
      <TonZeichen ton={ueber ? "kritisch" : "warnung"} />
      {new Date(`${datum}T12:00:00`).toLocaleDateString("de-DE")}
    </span>
  );
}

/**
 * Die Kachel eines Moduls. Das Modul liefert Daten nach dem Vertrag; wie sie
 * aussehen, entscheidet die Oberflaeche. Passt die Antwort nicht zum Vertrag,
 * steht das hier -- statt dass die ganze Uebersicht ausfaellt.
 */
function ModulKarte({ kachel }: { kachel: ModulKachel }) {
  const { data, isError } = useQuery({
    queryKey: ["kachel", kachel.url],
    queryFn: async () => DashboardKachel.safeParse(await anfrage<unknown>("GET", kachel.url)),
  });

  if (isError) {
    return (
      <Karte titel={kachel.titel}>
        <Leer>Das Modul „{kachel.modul}“ antwortet gerade nicht.</Leer>
      </Karte>
    );
  }
  if (data === undefined) return null;
  if (!data.success) {
    return (
      <Karte titel={kachel.titel}>
        <Leer>Das Modul „{kachel.modul}“ liefert Daten in unerwarteter Form.</Leer>
      </Karte>
    );
  }

  const k = data.data;
  switch (k.art) {
    case "kennzahlen":
      return (
        <Karte titel={k.titel}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {k.eintraege.map((e) => (
              <div key={e.bezeichnung} className="rounded-xl bg-neutral-100 dark:bg-neutral-900">
                <Kennzahl
                  bezeichnung={e.bezeichnung}
                  wert={e.wert}
                  einheit={e.einheit}
                  ton={e.ton}
                  link={e.link}
                />
              </div>
            ))}
          </div>
        </Karte>
      );
    case "verteilung":
      return (
        <Karte titel={k.titel}>
          <Balken
            eintraege={k.eintraege.map((e) => ({
              name: e.bezeichnung,
              wert: e.wert,
              link: e.link,
            }))}
          />
        </Karte>
      );
    case "zeitreihe":
      return (
        <Karte titel={k.titel}>
          <Saeulen
            tage={k.punkte.map((pt) => ({ datum: pt.datum, werte: [pt.wert] }))}
            reihen={[{ name: k.einheit ?? "Anzahl", farbe: "var(--reihe-1)" }]}
          />
        </Karte>
      );
    case "liste":
      return (
        <Karte titel={k.titel}>
          {k.zeilen.length === 0 ? (
            <Leer>{k.leer}</Leer>
          ) : (
            <ul className="flex flex-col gap-1">
              {k.zeilen.map((z, i) => {
                const inhalt = (
                  <>
                    <span className="flex items-center gap-2">
                      <TonZeichen ton={z.ton} />
                      <span className="font-semibold">{z.text}</span>
                    </span>
                    {z.zusatz !== undefined && (
                      <span className="text-sm text-neutral-600 dark:text-neutral-300">
                        {z.zusatz}
                      </span>
                    )}
                  </>
                );
                const klasse = "flex min-h-12 items-center justify-between gap-3 rounded-lg px-2";
                return (
                  <li key={i}>
                    {z.link === undefined ? (
                      <div className={klasse}>{inhalt}</div>
                    ) : (
                      <Link
                        to={z.link}
                        className={`${klasse} active:bg-neutral-100 dark:active:bg-neutral-700`}
                      >
                        {inhalt}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Karte>
      );
  }
}
